import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  MapPin, 
  Clock, 
  Users, 
  Heart, 
  Zap, 
  Globe, 
  Award,
  CheckCircle,
  Search
} from 'lucide-react';

const Careers: React.FC = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const departments = [
    { id: 'all', label: 'All Departments' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'design', label: 'Design' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'sales', label: 'Sales' },
    { id: 'operations', label: 'Operations' }
  ];

  const benefits = [
    {
      icon: <Heart className="h-8 w-8" />,
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance, mental health support, and wellness programs.'
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: 'Learning & Growth',
      description: 'Professional development budget, conference attendance, and skill-building workshops.'
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: 'Remote Work',
      description: 'Flexible remote work options and modern collaboration tools.'
    },
    {
      icon: <Award className="h-8 w-8" />,
      title: 'Recognition',
      description: 'Performance bonuses, employee recognition programs, and career advancement opportunities.'
    }
  ];

  const values = [
    {
      title: 'Innovation First',
      description: 'We encourage creative thinking and embrace new technologies to solve complex problems.'
    },
    {
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and open communication across all levels.'
    },
    {
      title: 'Excellence',
      description: 'We strive for the highest quality in everything we do, from code to client relationships.'
    },
    {
      title: 'Growth Mindset',
      description: 'We embrace challenges as opportunities to learn and improve continuously.'
    }
  ];

  const jobs = [
    {
      id: '1',
      title: 'Senior Full Stack Developer',
      department: 'engineering',
      location: 'Remote / Lahore, Pakistan',
      type: 'Full-time',
      experience: '3-5 years',
      description: 'We are looking for a Senior Full Stack Developer to join our growing team. You will work on cutting-edge web applications using React, Node.js, and modern development practices.',
      requirements: [
        '3+ years of experience with React and Node.js',
        'Proficiency in TypeScript and modern JavaScript',
        'Experience with databases (PostgreSQL, MongoDB)',
        'Knowledge of cloud platforms (AWS, Azure)',
        'Strong problem-solving and communication skills'
      ],
      posted: '2 days ago'
    },
    {
      id: '2',
      title: 'UI/UX Designer',
      department: 'design',
      location: 'Remote / Karachi, Pakistan',
      type: 'Full-time',
      experience: '2-4 years',
      description: 'Join our design team to create beautiful, user-centered digital experiences. You will work closely with developers and product managers to bring designs to life.',
      requirements: [
        '2+ years of UI/UX design experience',
        'Proficiency in Figma, Sketch, or Adobe XD',
        'Understanding of user research and testing',
        'Knowledge of design systems and component libraries',
        'Portfolio demonstrating strong design skills'
      ],
      posted: '1 week ago'
    },
    {
      id: '3',
      title: 'Digital Marketing Specialist',
      department: 'marketing',
      location: 'Remote / Islamabad, Pakistan',
      type: 'Full-time',
      experience: '2-3 years',
      description: 'We are seeking a Digital Marketing Specialist to drive our online presence and lead generation efforts across various digital channels.',
      requirements: [
        '2+ years of digital marketing experience',
        'Knowledge of SEO, SEM, and social media marketing',
        'Experience with Google Analytics and marketing automation',
        'Content creation and copywriting skills',
        'Analytical mindset with data-driven approach'
      ],
      posted: '3 days ago'
    },
    {
      id: '4',
      title: 'DevOps Engineer',
      department: 'engineering',
      location: 'Remote / Lahore, Pakistan',
      type: 'Full-time',
      experience: '3-5 years',
      description: 'Join our DevOps team to build and maintain scalable infrastructure, CI/CD pipelines, and deployment automation for our client projects.',
      requirements: [
        '3+ years of DevOps experience',
        'Proficiency with Docker, Kubernetes, and cloud platforms',
        'Experience with CI/CD tools (Jenkins, GitHub Actions)',
        'Knowledge of infrastructure as code (Terraform, CloudFormation)',
        'Strong scripting skills (Bash, Python)'
      ],
      posted: '5 days ago'
    },
    {
      id: '5',
      title: 'Business Development Manager',
      department: 'sales',
      location: 'Remote / Dubai, UAE',
      type: 'Full-time',
      experience: '4-6 years',
      description: 'We are looking for a Business Development Manager to expand our client base and drive revenue growth in the Middle East market.',
      requirements: [
        '4+ years of B2B sales experience in tech industry',
        'Proven track record of meeting sales targets',
        'Strong networking and relationship-building skills',
        'Knowledge of software development and digital solutions',
        'Excellent communication and presentation skills'
      ],
      posted: '1 week ago'
    },
    {
      id: '6',
      title: 'Project Manager',
      department: 'operations',
      location: 'Remote / Lahore, Pakistan',
      type: 'Full-time',
      experience: '3-4 years',
      description: 'Join our operations team to manage client projects, coordinate with development teams, and ensure successful project delivery.',
      requirements: [
        '3+ years of project management experience',
        'Experience with Agile/Scrum methodologies',
        'Strong organizational and leadership skills',
        'Knowledge of project management tools (Jira, Asana)',
        'Excellent client communication and stakeholder management'
      ],
      posted: '4 days ago'
    }
  ];

  const filteredJobs = jobs.filter(job => {
    const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesDepartment && matchesSearch;
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-12">
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30">
                💼 Career Opportunities
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
              Join Our <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Team</span>
            </h1>
            <p className="text-lg lg:text-xl mb-8 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              Be part of a dynamic team that's shaping the future of digital solutions. Work on exciting projects, grow your skills, and make a real impact.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">15+</div>
                <div className="text-blue-200 text-sm">Open Positions</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Remote</div>
                <div className="text-blue-200 text-sm">Work Options</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Growth</div>
                <div className="text-blue-200 text-sm">Opportunities</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">Benefits</div>
                <div className="text-blue-200 text-sm">Package</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Culture */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-purple-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium mb-6">
              💼 Why Choose Us?
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Why Work at <span className="text-purple-600 dark:text-purple-400">Infurasoft?</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We're not just a company - we're a community of innovators, problem-solvers, and digital pioneers who are passionate about creating exceptional experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-purple-200 dark:hover:border-purple-600 hover:-translate-y-3 hover:scale-105">
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-transparent dark:from-purple-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 text-white rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                      {benefit.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                      {benefit.description}
                    </p>
                  </div>
                  
                  {/* Hover Effect Border */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-purple-200 dark:hover:border-purple-600 hover:-translate-y-2">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-transparent dark:from-purple-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    {value.description}
                  </p>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-purple-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-orange-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-orange-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200 rounded-full text-sm font-medium mb-6">
              💼 Open Positions
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              <span className="text-orange-600 dark:text-orange-400">Join Our Team</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Explore exciting career opportunities and find the perfect role to advance your career with our dynamic and innovative team.
            </p>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col lg:flex-row gap-8 items-center justify-between mb-12">
            {/* Search */}
            <div className="relative w-full lg:w-96">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-red-600/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-200/50 dark:border-gray-600/50 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-lg hover:shadow-xl transition-all duration-300"
                />
              </div>
            </div>

            {/* Department Filters */}
            <div className="flex flex-wrap gap-2">
              {departments.map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => setSelectedDepartment(dept.id)}
                  className={`group px-3 py-2 rounded-xl text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg ${
                    selectedDepartment === dept.id
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white scale-105'
                      : 'bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 hover:scale-105 border border-gray-200/50 dark:border-gray-600/50'
                  }`}
                >
                  <span className="group-hover:scale-110 transition-transform duration-300 inline-block">
                    {dept.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Job Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {filteredJobs.map((job) => (
              <div key={job.id} className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-orange-200 dark:hover:border-orange-600 hover:-translate-y-1 hover:scale-102 mx-4">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-transparent dark:from-orange-900/20 dark:to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg">
                          <MapPin className="h-3 w-3 mr-1 text-orange-600 dark:text-orange-400" />
                          <span className="font-medium">{job.location}</span>
                        </div>
                        <div className="flex items-center bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg">
                          <Clock className="h-3 w-3 mr-1 text-orange-600 dark:text-orange-400" />
                          <span className="font-medium">{job.type}</span>
                        </div>
                        <div className="flex items-center bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg">
                          <Users className="h-3 w-3 mr-1 text-orange-600 dark:text-orange-400" />
                          <span className="font-medium">{job.experience}</span>
                        </div>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/50 dark:to-red-900/50 text-orange-800 dark:text-orange-200 text-xs rounded-full font-medium border border-orange-200/50 dark:border-orange-700/50 group-hover:scale-105 transition-transform duration-300">
                      {job.department}
                    </span>
                  </div>

                  <p className="text-gray-600 dark:text-gray-300 mb-4 text-xs leading-relaxed line-clamp-2 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    {job.description}
                  </p>

                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white mb-2">Key Requirements:</h4>
                    <div className="space-y-1">
                      {job.requirements.slice(0, 2).map((req, index) => (
                        <div key={index} className="flex items-center text-xs text-gray-600 dark:text-gray-300 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                          <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                          <span className="line-clamp-1">{req}</span>
                        </div>
                      ))}
                      {job.requirements.length > 2 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          +{job.requirements.length - 2} more requirements
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                      Posted {job.posted}
                    </span>
                    <div className="flex gap-2">
                      <button className="group/btn px-3 py-1 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-semibold text-xs transition-colors duration-300">
                        <span>Details</span>
                      </button>
                      <button className="group/btn px-3 py-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white text-xs font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg">
                        <span>Apply</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-orange-500/20 to-red-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-3xl p-12 shadow-lg border border-gray-100/50 dark:border-gray-700/50">
                <div className="text-6xl mb-4">🔍</div>
                <div className="text-gray-500 dark:text-gray-400 text-xl font-medium">
                  No jobs found matching your criteria.
                </div>
                <p className="text-gray-400 dark:text-gray-500 mt-2">
                  Try adjusting your search or filter options.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30 mb-6">
              💼 Join Our Team
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Don't See the <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Perfect Role?</span>
          </h2>
          <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            We're always looking for talented individuals. Send us your resume and let's start a conversation about how you can contribute to our team.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <button className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl bg-white text-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105">
              <span>Send Your Resume</span>
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:scale-105"
            >
              <span>Get in Touch</span>
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Growth</div>
              <div className="text-blue-200 text-sm">Opportunities</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Flexible</div>
              <div className="text-blue-200 text-sm">Work Environment</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-200 text-sm">Support</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
